import useClickOutside from "@admin/hooks/useClickOutside";
import { cva } from "class-variance-authority";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Option } from "./Select";

interface MultiSelectDropdownProps {
  options: Option[];
  selectedOptions: Option[];
  onSelect?: (selected: Option[]) => void;
  name?: string; // This will be used for form submission
  size?: "sm" | "md" | "lg";
  variant?: "standart" | "filled" | "outlined";
  placeholder?: string;
}

const multiSelect = cva("relative  w-full", {
  variants: {
    size: {
      sm: "px-2 py-1 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    },
    variant: {
      standart: "",
      filled: "text-gray-700 rounded-md bg-light border border-gray-300",
      outlined: "bg-transparent border-0 focus:outline-none !px-0",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "standart",
  },
});

export function MultiSelectDropdown({
  options,
  selectedOptions,
  onSelect,
  size = "md",
  variant = "standart",
  placeholder,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const ref = useRef(null);

  useClickOutside(ref, () => setIsOpen(false));

  const handleSelect = (option: Option) => {
    const isSelected = selectedOptions?.some(
      (selected) => selected.value === option.value,
    );
    if (isSelected) {
      onSelect?.(
        selectedOptions.filter((selected) => selected.value !== option.value),
      );
    } else {
      onSelect?.([...selectedOptions, option]);
    }
  };

  const handleSelectAll = () => {
    if (selectedOptions.length === options.length) {
      onSelect?.([]);
    } else {
      onSelect?.(options);
    }
  };

  return (
    <div ref={ref} className={multiSelect({ size, variant })}>
      <div
        className="flex items-center justify-between  cursor-pointer"
        onClick={handleToggle}
      >
        <div className="text-gray-800 text-[13px]">
          {selectedOptions.length > 0
            ? selectedOptions.map((option) => option.label).join(", ")
            : placeholder}
        </div>
        <div className="text-gray-500">
          <svg
            className={`w-4 h-4 transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <ul className="absolute z-10 w-full left-0 top-[120%] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
          <li
            className="p-2 cursor-pointer hover:bg-gray-100 font-medium"
            onClick={handleSelectAll}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedOptions.length === options.length}
                readOnly
                className="mr-2"
              />
              {t("common.Select All")}
            </div>
          </li>
          {options.map((option) => (
            <li
              key={option.value}
              className={`p-2 cursor-pointer hover:bg-gray-100 ${
                selectedOptions.some(
                  (selected) => selected.value === option.value,
                )
                  ? "bg-blue-100"
                  : ""
              }`}
              onClick={() => handleSelect(option)}
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedOptions.some(
                    (selected) => selected.value === option.value,
                  )}
                  readOnly
                  className="mr-2"
                />
                {option.label}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
